import { Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter } from '@angular/material/core';
import 'moment/locale/ru';
import 'moment/locale/kk';

export type AppLanguage = 'en' | 'kk' | 'ru';

export interface LanguageOption {
  code: AppLanguage;
  label: string;
  nativeLabel: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'kk', label: 'Kazakh', nativeLabel: 'Қазақша' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский' },
];

const LANGUAGE_STORAGE_KEY = '1p-app-language';
const DEFAULT_LANGUAGE: AppLanguage = 'en';

const LANGUAGE_TO_LOCALE: Record<AppLanguage, string> = {
  en: 'en',
  ru: 'ru',
  kk: 'kk',
};

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly currentLanguage = signal<AppLanguage>(DEFAULT_LANGUAGE);

  constructor(
    private translate: TranslateService,
    private dateAdapter: DateAdapter<Date>,
  ) {
    this.translate.addLangs(SUPPORTED_LANGUAGES.map(l => l.code));
    this.translate.setDefaultLang(DEFAULT_LANGUAGE);

    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as AppLanguage | null;
    const initial = stored && SUPPORTED_LANGUAGES.some(l => l.code === stored)
      ? stored
      : DEFAULT_LANGUAGE;

    this.setLanguage(initial);
  }

  setLanguage(lang: AppLanguage): void {
    this.translate.use(lang);
    this.currentLanguage.set(lang);
    this.dateAdapter.setLocale(LANGUAGE_TO_LOCALE[lang]);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  }

  getSupportedLanguages(): LanguageOption[] {
    return SUPPORTED_LANGUAGES;
  }
}
